import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../hooks/useAuth";
import {
  buscarEmAndamento,
  iniciarCronometro,
  pararCronometro,
} from "../api/taskTimeApi";
import { segundosDecorridos } from "../utils/taskTime";

const TimerContext = createContext(null);

// O relogio anda no cliente; o servidor so e consultado de vez em quando para
// corrigir a deriva e capturar o que foi iniciado em outro dispositivo.
const INTERVALO_RESSINCRONIZAR_MS = 5 * 60 * 1000;

/**
 * Fonte unica do cronometro.
 *
 * Existe uma regra de produto por tras: uma pessoa so conta tempo em UMA tarefa
 * por vez. Com o estado espalhado por tela, duas listas abertas mostrariam
 * cronometros diferentes rodando ao mesmo tempo; centralizando aqui, o header,
 * a lista e o modal falam sempre do mesmo segmento.
 *
 * `versao` sobe a cada mudanca: as telas usam isso para recarregar os totais
 * sem precisar assinar evento nenhum.
 */
export const TimerProvider = ({ children }) => {
  const { isAuthenticated, loading: carregandoAuth } = useAuth();

  const [emAndamento, setEmAndamento] = useState(null);
  const [resumo, setResumo] = useState({ hoje_segundos: 0, semana_segundos: 0 });
  const [segundos, setSegundos] = useState(0);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState("");
  const [versao, setVersao] = useState(0);
  // Guarda o que foi pausado automaticamente para a tela conseguir avisar.
  const [ultimaTroca, setUltimaTroca] = useState(null);

  const emAndamentoRef = useRef(null);
  emAndamentoRef.current = emAndamento;

  const aplicar = useCallback((registro) => {
    setEmAndamento(registro || null);
    setSegundos(registro ? segundosDecorridos(registro.inicio) : 0);
    setVersao((atual) => atual + 1);
  }, []);

  const sincronizar = useCallback(async () => {
    if (!isAuthenticated) {
      setEmAndamento(null);
      setSegundos(0);
      return;
    }

    try {
      const { record, resumo: totais } = await buscarEmAndamento();
      setEmAndamento(record);
      setSegundos(record ? segundosDecorridos(record.inicio) : 0);
      if (totais) setResumo(totais);
      setErro("");
    } catch (error) {
      // Falhar aqui nao pode derrubar a tela: o cronometro e acessorio.
      console.error("TimerProvider: erro ao sincronizar cronometro", error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (carregandoAuth) return;
    sincronizar();
  }, [carregandoAuth, isAuthenticated, sincronizar]);

  // O tique so existe enquanto ha segmento aberto.
  useEffect(() => {
    if (!emAndamento) return undefined;

    const tique = window.setInterval(() => {
      setSegundos(segundosDecorridos(emAndamento.inicio));
    }, 1000);

    return () => window.clearInterval(tique);
  }, [emAndamento]);

  // Aba adormecida atrasa o setInterval e o relogio do servidor pode ter mudado
  // por outro dispositivo: ao voltar, vale mais reperguntar do que confiar.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const aoVoltar = () => {
      if (document.visibilityState === "visible") sincronizar();
    };

    const periodico = window.setInterval(sincronizar, INTERVALO_RESSINCRONIZAR_MS);
    document.addEventListener("visibilitychange", aoVoltar);

    return () => {
      window.clearInterval(periodico);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [isAuthenticated, sincronizar]);

  // De proposito nao ha aviso de "beforeunload" com o cronometro rodando: o
  // segmento ja esta gravado no servidor, entao fechar a aba nao perde nada -
  // avisar sugeriria um risco que nao existe. Ao voltar, o relogio continua.

  const iniciar = useCallback(
    async (tarefa) => {
      const taskId = tarefa?.id ?? tarefa;
      if (!taskId || ocupado) return null;

      setOcupado(true);
      setErro("");

      try {
        const { record, encerrada } = await iniciarCronometro(taskId);
        aplicar(record);
        setUltimaTroca(encerrada || null);
        return record;
      } catch (error) {
        console.error("TimerProvider: erro ao iniciar", error);
        setErro(error?.response?.data?.message || error?.message || "Erro ao iniciar o cronometro.");
        return null;
      } finally {
        setOcupado(false);
      }
    },
    [aplicar, ocupado]
  );

  const parar = useCallback(async () => {
    const atual = emAndamentoRef.current;
    if (!atual || ocupado) return null;

    setOcupado(true);
    setErro("");

    try {
      const encerrada = await pararCronometro(atual.id_tarefa);
      aplicar(null);
      setUltimaTroca(null);
      // O total do dia acabou de mudar; o widget mostra o numero novo.
      sincronizar();
      return encerrada;
    } catch (error) {
      console.error("TimerProvider: erro ao parar", error);
      setErro(error?.response?.data?.message || error?.message || "Erro ao parar o cronometro.");
      return null;
    } finally {
      setOcupado(false);
    }
  }, [aplicar, ocupado, sincronizar]);

  /** Um botao so na lista: clicar na tarefa que ja conta tempo pausa. */
  const alternar = useCallback(
    async (tarefa) => {
      const taskId = tarefa?.id ?? tarefa;
      const atual = emAndamentoRef.current;

      if (atual && Number(atual.id_tarefa) === Number(taskId)) return parar();
      return iniciar(taskId);
    },
    [iniciar, parar]
  );

  const estaRodando = useCallback(
    (taskId) =>
      Boolean(emAndamento) && Number(emAndamento.id_tarefa) === Number(taskId),
    [emAndamento]
  );

  const limparAviso = useCallback(() => setUltimaTroca(null), []);

  const valor = useMemo(
    () => ({
      emAndamento,
      segundos,
      resumo,
      ocupado,
      erro,
      versao,
      ultimaTroca,
      alternar,
      estaRodando,
      iniciar,
      limparAviso,
      parar,
      sincronizar,
    }),
    [
      emAndamento,
      segundos,
      resumo,
      ocupado,
      erro,
      versao,
      ultimaTroca,
      alternar,
      estaRodando,
      iniciar,
      limparAviso,
      parar,
      sincronizar,
    ]
  );

  return <TimerContext.Provider value={valor}>{children}</TimerContext.Provider>;
};

export const useTimer = () => {
  const contexto = useContext(TimerContext);

  if (!contexto) {
    throw new Error("useTimer deve ser usado dentro de TimerProvider.");
  }

  return contexto;
};
